const TOOLTIP_SELECTOR = '.c-tooltip, .w-tooltip, [data-tooltip-trigger="true"]';
const TOOLTIP_BUBBLE_ID = 'wombat-tooltip-bubble';
const TOOLTIP_VIEWPORT_GUTTER = 16;

let activeTooltip = null;
let tooltipBubble = null;

/**
 * Ensure the shared tooltip bubble exists.
 *
 * @return {HTMLElement} Tooltip bubble element.
 */
const getTooltipBubble = () => {

	if ( tooltipBubble ) {

		return tooltipBubble;

	}

	tooltipBubble = document.createElement( 'div' );
	tooltipBubble.className = 'c-tooltip-bubble';
	tooltipBubble.id = TOOLTIP_BUBBLE_ID;
	tooltipBubble.setAttribute( 'role', 'tooltip' );
	tooltipBubble.setAttribute( 'aria-hidden', 'true' );
	document.body.appendChild( tooltipBubble );

	return tooltipBubble;

};

/**
 * Resolve tooltip placement.
 *
 * @param {HTMLElement} tooltip Tooltip trigger.
 * @return {string} Placement.
 */
const getTooltipPlacement = ( tooltip ) => tooltip.dataset.tooltipPlacement === 'bottom' ? 'bottom' : 'top';

/**
 * Position the shared tooltip bubble for the active trigger.
 *
 * @param {HTMLElement} tooltip Tooltip trigger.
 * @return {void}
 */
const positionTooltipBubble = ( tooltip ) => {

	const bubble = getTooltipBubble();
	const tooltipRect = tooltip.getBoundingClientRect();
	const placement = getTooltipPlacement( tooltip );

	bubble.textContent = tooltip.dataset.tooltip || '';
	bubble.dataset.placement = placement;
	bubble.style.maxWidth = `${ window.innerWidth - ( TOOLTIP_VIEWPORT_GUTTER * 2 ) }px`;
	bubble.classList.add( 'is-visible' );

	const bubbleRect = bubble.getBoundingClientRect();
	const centeredLeft = tooltipRect.left + ( tooltipRect.width / 2 ) - ( bubbleRect.width / 2 );
	const clampedLeft = Math.min(
		Math.max( TOOLTIP_VIEWPORT_GUTTER, centeredLeft ),
		window.innerWidth - TOOLTIP_VIEWPORT_GUTTER - bubbleRect.width
	);
	const arrowLeft = ( tooltipRect.left + ( tooltipRect.width / 2 ) ) - clampedLeft;
	const targetTop = placement === 'bottom'
		? tooltipRect.bottom + 11
		: tooltipRect.top - bubbleRect.height - 11;
	const clampedTop = Math.min(
		Math.max( TOOLTIP_VIEWPORT_GUTTER, targetTop ),
		window.innerHeight - TOOLTIP_VIEWPORT_GUTTER - bubbleRect.height
	);

	bubble.style.left = `${ clampedLeft }px`;
	bubble.style.top = `${ clampedTop }px`;
	bubble.style.setProperty( '--tooltip-arrow-left', `${ arrowLeft }px` );

};

/**
 * Close every currently opened tooltip.
 *
 * @return {void}
 */
const closeAllTooltips = () => {

	const bubble = getTooltipBubble();

	document.querySelectorAll( `${ TOOLTIP_SELECTOR }[data-open="true"]` ).forEach( ( tooltip ) => {

		tooltip.dataset.open = 'false';
		tooltip.removeAttribute( 'aria-describedby' );

		if ( tooltip.hasAttribute( 'aria-expanded' ) ) {

			tooltip.setAttribute( 'aria-expanded', 'false' );

		}

	} );

	activeTooltip = null;
	bubble.classList.remove( 'is-visible' );
	bubble.setAttribute( 'aria-hidden', 'true' );

};

/**
 * Open a tooltip and position the shared bubble.
 *
 * @param {HTMLElement} tooltip Tooltip trigger.
 * @return {void}
 */
const openTooltip = ( tooltip ) => {

	const bubble = getTooltipBubble();

	activeTooltip = tooltip;
	tooltip.dataset.open = 'true';
	tooltip.setAttribute( 'aria-describedby', TOOLTIP_BUBBLE_ID );

	if ( tooltip.hasAttribute( 'aria-expanded' ) ) {

		tooltip.setAttribute( 'aria-expanded', 'true' );

	}

	positionTooltipBubble( tooltip );
	bubble.setAttribute( 'aria-hidden', 'false' );

};

document.addEventListener( 'click', ( event ) => {

	const tooltip = event.target.closest( TOOLTIP_SELECTOR );
	if ( ! tooltip ) {

		closeAllTooltips();
		return;

	}

	const nextState = tooltip.dataset.open === 'true' ? 'false' : 'true';

	closeAllTooltips();
	if ( nextState === 'true' ) {

		openTooltip( tooltip );

	}

} );

document.addEventListener( 'keydown', ( event ) => {

	if ( event.key === 'Escape' ) {

		closeAllTooltips();
		return;

	}

	const tooltip = event.target.closest( TOOLTIP_SELECTOR );

	if ( ! tooltip || tooltip.tagName === 'BUTTON' ) {

		return;

	}

	if ( event.key !== 'Enter' && event.key !== ' ' ) {

		return;

	}

	event.preventDefault();

	const nextState = tooltip.dataset.open === 'true' ? 'false' : 'true';

	closeAllTooltips();
	if ( nextState === 'true' ) {

		openTooltip( tooltip );

	}

} );

document.addEventListener( 'focusin', ( event ) => {

	const tooltip = event.target.closest( TOOLTIP_SELECTOR );
	if ( ! tooltip ) {

		return;

	}

	closeAllTooltips();
	openTooltip( tooltip );

} );

document.addEventListener( 'focusout', ( event ) => {

	const tooltip = event.target.closest( TOOLTIP_SELECTOR );
	if ( ! tooltip ) {

		return;

	}

	tooltip.dataset.open = 'false';
	tooltip.removeAttribute( 'aria-describedby' );
	if ( tooltip.hasAttribute( 'aria-expanded' ) ) {

		tooltip.setAttribute( 'aria-expanded', 'false' );

	}
	if ( activeTooltip === tooltip ) {

		activeTooltip = null;
		getTooltipBubble().classList.remove( 'is-visible' );
		getTooltipBubble().setAttribute( 'aria-hidden', 'true' );

	}

} );

document.addEventListener( 'mouseover', ( event ) => {

	const tooltip = event.target.closest( TOOLTIP_SELECTOR );
	if ( ! tooltip ) {

		return;

	}

	closeAllTooltips();
	openTooltip( tooltip );

} );

document.addEventListener( 'mouseout', ( event ) => {

	const tooltip = event.target.closest( TOOLTIP_SELECTOR );
	if ( ! tooltip || tooltip.contains( event.relatedTarget ) ) {

		return;

	}

	if ( activeTooltip === tooltip ) {

		closeAllTooltips();

	}

} );

window.addEventListener( 'resize', () => {

	if ( activeTooltip ) {

		positionTooltipBubble( activeTooltip );

	}

} );

window.addEventListener( 'scroll', () => {

	if ( activeTooltip ) {

		positionTooltipBubble( activeTooltip );

	}

}, true );
